import React, { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

const PAPER = '#F0EDE8'
const brand = {
  tapeTurquoise: '#0D3135',
  sandrift: '#AB9381',
  drillbitSkies: '#4A7D83',
  plaster: '#CECAC3',
}
const logoUrl = 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson-stacked-dark.png'

const userNav = [
  { to: '/portal', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { to: '/portal/snapshot', label: 'Snapshot', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
  { to: '/portal/blueprint', label: 'Blueprint', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { to: '/portal/requests', label: 'Requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { to: '/portal/vault', label: 'Vault', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
  { to: '/portal/schedule', label: 'Schedule', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { to: '/portal/property', label: 'Property', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { to: '/portal/membership', label: 'Services', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
]

const adminNav = [
  { to: '/portal/admin', label: 'Admin', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
]

function SvgIcon({ d }: { d: string }) {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  )
}

export default function PortalLayout() {
  const { profile, signOut, isAdmin } = useAuth()
  const navigate = useNavigate()
  const navItems = isAdmin ? [...userNav, ...adminNav] : userNav

  const handleSignOut = async () => {
    await signOut()
    navigate('/portal/login')
  }

  return (
    <div className="flex h-screen" style={{ backgroundColor: PAPER }}>
      {/* Desktop sidebar */}
      <aside className="hidden w-[260px] flex-col lg:flex" style={{ backgroundColor: brand.tapeTurquoise }}>
        <div className="px-7 pt-8 pb-6">
          <NavLink to="/portal">
            <img src={logoUrl} alt="Hudson My Home" className="h-8 w-auto object-contain brightness-0 invert opacity-90" />
          </NavLink>
        </div>

        <div className="mx-5 h-px mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-4">
          {navItems.map(item => (
            <NavLink
              key={item.to} to={item.to} end={item.to === '/portal' || item.to === '/portal/admin'}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 font-sans text-sm tracking-wide transition-all duration-200 ${
                  isActive
                    ? 'bg-white/10 text-white font-medium'
                    : 'text-white/50 hover:bg-white/5 hover:text-white/80'
                }`
              }
            >
              <SvgIcon d={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mx-5 h-px mt-4" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

        <div className="p-5">
          <div className="mb-3 truncate font-sans text-sm font-medium text-white/80">
            {profile?.full_name || profile?.email || 'User'}
          </div>
          <button
            onClick={handleSignOut}
            className="w-full py-2.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-white/40 transition hover:text-white/70"
            style={{ border: '1px solid rgba(255,255,255,0.1)' }}
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="flex h-16 items-center justify-between px-5 lg:hidden" style={{ backgroundColor: brand.tapeTurquoise }}>
          <NavLink to="/portal">
            <img src={logoUrl} alt="Hudson" className="h-7 w-auto brightness-0 invert opacity-90" />
          </NavLink>
          <button onClick={handleSignOut} className="font-sans text-xs font-medium uppercase tracking-wider text-white/50">
            Sign Out
          </button>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8 lg:px-12 lg:py-12">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom bar */}
        <MobileBottomNav navItems={navItems} />
      </div>
    </div>
  )
}

function MobileBottomNav({ navItems }: { navItems: typeof userNav }) {
  const [showMore, setShowMore] = useState(false)
  const primaryItems = navItems.slice(0, 4)
  const overflowItems = navItems.slice(4)

  return (
    <>
      {showMore && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setShowMore(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute bottom-16 left-0 right-0 rounded-t-2xl p-4 pb-6 space-y-1"
            style={{ backgroundColor: brand.tapeTurquoise }}
            onClick={e => e.stopPropagation()}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 px-3 py-2">More</p>
            {overflowItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/portal' || item.to === '/portal/admin'}
                onClick={() => setShowMore(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-3 font-sans text-sm tracking-wide transition ${
                    isActive ? 'bg-white/10 text-white font-medium' : 'text-white/60'
                  }`
                }
              >
                <SvgIcon d={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      <nav className="flex lg:hidden" style={{ backgroundColor: brand.tapeTurquoise, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {primaryItems.map(item => (
          <NavLink
            key={item.to} to={item.to} end={item.to === '/portal' || item.to === '/portal/admin'}
            onClick={() => setShowMore(false)}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-1 py-2.5 font-sans text-[10px] font-medium tracking-wide transition ${
                isActive ? 'text-white' : 'text-white/35'
              }`
            }
          >
            <SvgIcon d={item.icon} />
            {item.label}
          </NavLink>
        ))}
        {overflowItems.length > 0 && (
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-1 flex-col items-center gap-1 py-2.5 font-sans text-[10px] font-medium tracking-wide transition ${showMore ? 'text-white' : 'text-white/35'}`}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01" />
            </svg>
            More
          </button>
        )}
      </nav>
    </>
  )
}
